import { FC, useState } from 'react'
import Modal from 'react-modal'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as LinkedInIcon } from 'src/assets/svg/linkedin.svg'
import { ReactComponent as XIcon } from 'src/assets/svg/x.svg'
import { ReactComponent as WhatsappIcon } from 'src/assets/svg/whatsapp.svg'
import { ReactComponent as BookingIcon } from 'src/assets/svg/booking-brand.svg'
import { ReactComponent as ExpediaIcon } from 'src/assets/svg/expedia-brand.svg'
import { InboxAnalysisPage } from 'src/reactQuery/useGetInboxAnalysisPagesQuery'
import classes from './disconnectPageModal.module.scss'

interface DisconnectPageModalProps {
  isOpen: boolean
  onCancel: () => void
  onConfirm: () => void
  page: InboxAnalysisPage | null
}

const platformIcon = (platform: string) => {
  switch (platform) {
    case 'facebook':
      return <FacebookIcon className={classes.platformIcon} />
    case 'instagram':
      return <InstagramIcon className={classes.platformIcon} />
    case 'twitter':
      return <XIcon className={classes.platformIcon} />
    case 'linkedin':
      return <LinkedInIcon className={classes.platformIcon} />
    case 'whatsapp':
      return <WhatsappIcon className={classes.platformIcon} />
    case 'booking':
      return <BookingIcon className={classes.platformIcon} />
    case 'expedia':
      return <ExpediaIcon className={classes.platformIcon} />
    default:
      return null
  }
}

export const DisconnectPageModal: FC<DisconnectPageModalProps> = ({
  isOpen,
  onCancel,
  onConfirm,
  page,
}) => {
  const [selected, setSelected] = useState<string[]>([])

  const platforms = page?.platforms ?? []

  const togglePlatform = (platform: string) => {
    setSelected((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    )
  }

  const selectAll = () => {
    setSelected(selected.length === platforms.length ? [] : [...platforms])
  }

  const handleConfirm = () => {
    setSelected([])
    onConfirm()
  }

  const handleCancel = () => {
    setSelected([])
    onCancel()
  }

  return (
    <Modal
      className={classes.modal}
      isOpen={isOpen}
      overlayClassName={classes.overlay}
      parentSelector={() => document.body}
    >
      <button
        className={classes.closeButton}
        onClick={handleCancel}
        type="button"
      >
        ✕
      </button>

      <h2 className={classes.title}>Delete Connected Page?</h2>
      <p className={classes.description}>
        This page will be disconnected. You can connect it again anytime, but
        its current data and insights will be removed.
      </p>

      <div className={classes.platformSection}>
        <div className={classes.platformHeader}>
          <span className={classes.platformLabel}>Choose Platform:</span>
          <button
            className={classes.selectAll}
            onClick={selectAll}
            type="button"
          >
            Select All
          </button>
        </div>

        <div className={classes.platformList}>
          {platforms.map((platform) => (
            <div
              className={classes.platformItem}
              key={platform}
              onClick={() => togglePlatform(platform)}
            >
              <div className={classes.platformLeft}>
                {platformIcon(platform)}
                <span className={classes.platformName}>{page?.name}</span>
                {page?.handle && (
                  <span className={classes.platformHandle}>
                    ({page.handle})
                  </span>
                )}
              </div>
              <div
                className={`${classes.checkbox} ${selected.includes(platform) ? classes.checked : ''}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className={classes.footer}>
        <button
          className={classes.cancelButton}
          onClick={handleCancel}
          type="button"
        >
          Cancel
        </button>
        <button
          className={classes.deleteButton}
          disabled={selected.length === 0}
          onClick={handleConfirm}
          type="button"
        >
          Delete Page
        </button>
      </div>
    </Modal>
  )
}
